import React, { useEffect, useRef, useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, Typography } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { ScribeButton as Button } from '../../components/scribe/ScribeUI';
import { recordClientConsent, uploadConsentEvidence, validateConsentEvidence } from '../../services/clientConsentService';
import ConsentDocumentPicker from './ConsentDocumentPicker';
const localTime = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
};
export default function ClientConsentDialog({
  patientId,
  target,
  mode = 'consent_granted',
  onClose,
  onSaved
}) {
  const [channel, setChannel] = useState(target?.channel || 'verbal'),
    [time, setTime] = useState(localTime),
    [notes, setNotes] = useState(''),
    [selection, setSelection] = useState(null);
  const [busy, setBusy] = useState(false),
    [attempted, setAttempted] = useState(false),
    [error, setError] = useState('');
  const eventId = useRef(uuid()),
    document = useRef(null),
    lock = useRef(false),
    mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const withdrawing = mode === 'consent_revoked',
    attaching = mode === 'document_attached';
  const title = withdrawing ? 'Record consent withdrawal' : attaching ? 'Attach consent document' : 'Record client consent';
  const save = async e => {
    e?.preventDefault();
    if (lock.current) return;
    setError('');
    const date = new Date(time);
    if (!time || !Number.isFinite(date.getTime()) || date.getTime() > Date.now() + 300000) {
      setError(withdrawing ? 'Enter a valid withdrawal date that is not in the future.' : 'Enter a valid consent date that is not in the future.');
      return;
    }
    if (target?.occurred_at && date < new Date(target.occurred_at)) {
      setError('The date cannot be before the original consent date.');
      return;
    }
    if (attaching && !selection) {
      setError('Select a consent document to attach.');
      return;
    }
    try {
      if (selection) validateConsentEvidence(selection);
    } catch (e) {
      setError(e.message);
      return;
    }
    lock.current = true;
    setBusy(true);
    setAttempted(true);
    try {
      if (selection && !document.current) document.current = await uploadConsentEvidence(patientId, eventId.current, selection);
      await recordClientConsent({
        patientId,
        eventId: eventId.current,
        kind: mode,
        occurredAt: date.toISOString(),
        channel,
        notes,
        consentId: target?.consent_id || target?.id,
        document: document.current
      });
      if (mounted.current) onSaved();
    } catch (e) {
      if (mounted.current) setError(e.message || 'Unable to save consent. Please retry.');
    } finally {
      lock.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return <Dialog open fullWidth maxWidth="sm" onClose={() => {
    if (!busy) onClose();
  }} aria-labelledby="consent-dialog-title">
  <DialogTitle id="consent-dialog-title">{title}</DialogTitle>
  <DialogContent><Stack component="form" id="consent-form" noValidate onSubmit={save} spacing={2} sx={{
        pt: 1
      }}>
   <Typography variant="body2" color="text.secondary">{withdrawing ? 'This records withdrawal without deleting the original consent or changing MindVault sharing.' : 'Record consent obtained from the client. This does not change MindVault sharing settings.'}</Typography>
   <TextField label={withdrawing ? 'Withdrawal date' : 'Consent date'} type="datetime-local" value={time} onChange={e => setTime(e.target.value)} InputLabelProps={{
          shrink: true
        }} inputProps={{
          step: 1
        }} disabled={attempted} fullWidth helperText={`Defaults to now. You can enter the actual date and time (${Intl.DateTimeFormat().resolvedOptions().timeZone}).`} />
   {!target && <TextField label="Consent channel" select SelectProps={{
          native: true
        }} value={channel} disabled={attempted} onChange={e => {
          setChannel(e.target.value);
          setSelection(null);
        }} fullWidth>
    <option value="verbal">Verbal</option><option value="written">Written</option>
   </TextField>}
   {target && <Typography variant="body2">Consent channel: {channel === 'written' ? 'Written' : 'Verbal'}</Typography>}
   {!attaching && <TextField label={withdrawing ? 'Withdrawal details (optional)' : 'Additional notes (optional)'} multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)} inputProps={{
          maxLength: 4000
        }} disabled={attempted} fullWidth />}
   {!withdrawing && channel === 'written' && <ConsentDocumentPicker patientId={patientId} selection={selection} onChange={setSelection} disabled={attempted} />}
   {channel === 'verbal' && !withdrawing && <Typography variant="body2" color="text.secondary">No document or audio upload is required for verbal consent.</Typography>}
   {error && <Alert severity="error">{error}{attempted && <Typography variant="caption" component="p">Retry this entry, or cancel to reload the log before making changes.</Typography>}</Alert>}
  </Stack></DialogContent>
  <DialogActions sx={{
      px: 3,
      pb: 2
    }}><Button disabled={busy} onClick={onClose}>Cancel</Button><Button variant="contained" type="submit" form="consent-form" disabled={busy}>
   {busy ? 'Saving…' : withdrawing ? 'Save withdrawal' : attaching ? 'Attach document' : 'Save consent'}
  </Button></DialogActions>
 </Dialog>;
}
